import * as XLSX from 'xlsx';
import type { ParseResult } from './parseResult';

// Reexport del tipo compartido (Task 12) para los consumidores de este módulo (Task 21).
export type { ParseResult } from './parseResult';

export interface TemplatePositionRow {
  positionId: string;
  date: string;
  pdvCode: string;
  pdvName: string;
  startTime: string;
  endTime: string;
}

export interface ParsedAssignmentRow {
  positionId: string;
  dni: string;
  rowNumber: number;
}

const SHEET_NAME = 'Asignaciones';
const HEADERS = ['Posicion ID', 'Fecha', 'Codigo PDV', 'PDV', 'Hora inicio', 'Hora fin', 'DNI'];

/** Genera el .xlsx de plantilla: una fila por posición abierta, columna DNI vacía. */
export function buildAssignmentsTemplate(positions: TemplatePositionRow[]): ArrayBuffer {
  const rows = positions.map((p) => [
    p.positionId, p.date, p.pdvCode, p.pdvName, p.startTime, p.endTime, '',
  ]);
  const ws = XLSX.utils.aoa_to_sheet([HEADERS, ...rows]);
  ws['!cols'] = [
    { wch: 38 }, { wch: 12 }, { wch: 12 }, { wch: 32 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, SHEET_NAME);
  return XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
}

/**
 * Lee la plantilla llena: solo Posicion ID (col A) y DNI (col G).
 * Filas sin DNI se ignoran. DNI debe tener 8 dígitos; Posicion ID no puede estar vacío.
 * rowNumber = número de fila en la hoja (1 = encabezado).
 */
export function parseAssignmentsFile(data: ArrayBuffer): ParseResult<ParsedAssignmentRow> {
  const rows: ParsedAssignmentRow[] = [];
  const errors: { rowNumber: number; message: string }[] = [];
  const wb = XLSX.read(data, { type: 'array' });
  const ws = wb.Sheets[SHEET_NAME];
  if (!ws) {
    return { rows, errors: [{ rowNumber: 0, message: 'No se encontró la hoja "Asignaciones"' }] };
  }
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '' });
  for (let i = 1; i < matrix.length; i++) {
    const rowNumber = i + 1;
    const row = matrix[i] ?? [];
    const dniRaw = row[6];
    const dni = dniRaw === null || dniRaw === undefined ? '' : String(dniRaw).trim();
    if (dni === '') continue; // fila sin DNI: se ignora
    const posRaw = row[0];
    const positionId = posRaw === null || posRaw === undefined ? '' : String(posRaw).trim();
    if (positionId === '') {
      errors.push({ rowNumber, message: 'Posicion ID vacío' });
      continue;
    }
    if (!/^\d{8}$/.test(dni)) {
      errors.push({ rowNumber, message: `DNI inválido "${dni}": debe tener 8 dígitos` });
      continue;
    }
    rows.push({ positionId, dni, rowNumber });
  }
  return { rows, errors };
}
